/**
 * Team (`/team`, TMMRSL-0030, H §5 reseller-wide): the reseller's own colleagues on saas-auth.
 *
 *  - **Colleagues** — `GET /api/reseller-admin/users?q=&page=&size=` (searched and paged on the
 *    server): name, email, role, status, two-step sign-in, created. OWNER / ADMIN act on a row:
 *    change role, suspend and remove (each confirmed), reactivate (one click).
 *  - **Invitations** — `GET /invitations?status=`, resend / revoke. saas-auth lets only OWNER /
 *    ADMIN read them, so STAFF does not get the tab.
 *  - **Invite colleague** — `POST /users { email, role }`, always `202`: a neutral toast.
 *
 * An ADMIN can never grant OWNER nor act on an OWNER (or an OWNER invitation), so those options
 * and rows are hidden for an ADMIN; saas-auth still answers `403 RESELLER_ROLE_NOT_GRANTABLE`.
 * `409 RESELLER_LAST_OWNER` is shown inline. STAFF is read-only: no actions at all.
 */
import { useCallback, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  saasAuthResellerApi,
  type Colleague,
  type ColleagueStatus,
  type Invitation,
} from '@/api/saasAuthReseller';
import type { ResellerRole } from '@/auth/authContextValue';
import { useCanWrite, useCurrentOperator } from '@/auth/authHooks';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useToast } from '@/components/useToast';
import { useLocale } from '@/i18n/useLocale';
import { enumParam, useUrlState } from '@/components/useUrlState';
import { INVITATIONS_PARAM, InvitationsTable } from '@/features/members/InvitationsTable';
import { InviteMemberDialog } from '@/features/members/InviteMemberDialog';
import { useMemberError, useMemberErrorMessage, type MemberError } from '@/features/members/memberErrors';
import { StatusChip } from '@/components/status/StatusChip';
import { dayOf, roleLabel } from '@/features/members/memberLabels';
import { Pager, SearchField } from '@/features/members/PagedSearch';
import { RoleDialog, type RoleOption } from '@/features/members/RoleDialog';
import { RowActionsMenu, type RowAction } from '@/features/members/RowActionsMenu';
import { useMenuFocusReturn } from '@/features/members/useMenuFocusReturn';
import { usePagedSearch } from '@/features/members/usePagedSearch';
import { useRemote } from '@/components/useRemote';

const ALL_ROLES: readonly ResellerRole[] = ['OWNER', 'ADMIN', 'STAFF'];

type TeamTab = 'colleagues' | 'invitations';

/** The tab in the URL; a switch drops the old tab's search, page, size and status (TMM-B6). */
const TAB_SCHEMA = { tab: enumParam<TeamTab>(['colleagues', 'invitations'], 'colleagues') };
const TAB_OPTIONS = { clears: ['q', 'page', 'size', INVITATIONS_PARAM] };

export function TeamScreen() {
  const { t } = useTranslation();
  const canWrite = useCanWrite();
  const operator = useCurrentOperator();
  const isOwner = operator?.isOwner === true;
  const tabState = useUrlState(TAB_SCHEMA, TAB_OPTIONS);
  const { tab } = tabState.values;
  const setTab = (next: TeamTab) => tabState.set({ tab: next });
  const [inviting, setInviting] = useState(false);
  const [invitationsKey, setInvitationsKey] = useState(0);

  // An ADMIN can never grant OWNER (H §5) — the option is not offered.
  const grantable: RoleOption[] = ALL_ROLES.filter((role) => isOwner || role !== 'OWNER').map(
    (role) => ({ value: role, label: t(`roles.${role}`) }),
  );
  // …nor act on an OWNER, or an OWNER invitation.
  const mayActOn = (role: string | null) => canWrite && (isOwner || role !== 'OWNER');

  const canActOnInvitation = useCallback(
    (invitation: Invitation) =>
      canWrite && invitation.status === 'PENDING' && (isOwner || invitation.role !== 'OWNER'),
    [canWrite, isOwner],
  );
  const listInvitations = useCallback(
    (status: string | undefined) => saasAuthResellerApi.listColleagueInvitations(status),
    [],
  );

  const showInvitations = canWrite && tab === 'invitations';

  return (
    <Box>
      <PageHeader
        id="team"
        actions={
          canWrite ? (
            <Button variant="contained" onClick={() => setInviting(true)}>
              {t('team.invite')}
            </Button>
          ) : null
        }
      />
      <Tabs
        value={showInvitations ? 'invitations' : 'colleagues'}
        onChange={(_, next: TeamTab) => setTab(next)}
        aria-label={t('team.tabsLabel')}
        sx={{ mb: 2 }}
      >
        <Tab value="colleagues" label={t('team.tab.colleagues')} />
        {canWrite ? <Tab value="invitations" label={t('team.tab.invitations')} /> : null}
      </Tabs>
      {showInvitations ? (
        <InvitationsTable
          key={invitationsKey}
          list={listInvitations}
          resend={(id) => saasAuthResellerApi.resendColleagueInvitation(id)}
          revoke={(id) => saasAuthResellerApi.revokeColleagueInvitation(id)}
          canAct={canActOnInvitation}
          onInvite={() => setInviting(true)}
        />
      ) : (
        <Colleagues mayActOn={mayActOn} grantable={grantable} />
      )}
      <InviteMemberDialog
        open={inviting}
        title={t('team.inviteTitle')}
        roles={grantable}
        defaultRole="STAFF"
        onInvite={({ email, role }) =>
          saasAuthResellerApi.inviteColleague({ email, role: role as ResellerRole })
        }
        onClose={() => setInviting(false)}
        onDone={() => {
          setInvitationsKey((key) => key + 1);
          setTab('invitations');
        }}
      />
    </Box>
  );
}

type Pending =
  | { kind: 'role'; colleague: Colleague }
  | { kind: 'suspend'; colleague: Colleague }
  | { kind: 'remove'; colleague: Colleague }
  | null;

function Colleagues({
  mayActOn,
  grantable,
}: {
  mayActOn: (role: string | null) => boolean;
  grantable: RoleOption[];
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const errorMessage = useMemberErrorMessage();
  const memberError = useMemberError();
  const paged = usePagedSearch();
  const { q, page, size } = paged;
  const load = useCallback(
    () => saasAuthResellerApi.listColleagues({ q, page, size }),
    [q, page, size],
  );
  const { status, data, error, reload } = useRemote(load);
  const operator = useCurrentOperator();
  const [pending, setPending] = useState<Pending>(null);
  const [actionError, setActionError] = useState<MemberError | null>(null);
  // Colleagues whose status request is running: their menu item is disabled, and a ref guards a
  // second fire before the state has rendered (PERF-006).
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  const inFlight = useRef(new Set<number>());
  const heading = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const focusReturn = useMenuFocusReturn(heading);
  const rows = data?.content ?? [];
  const isSelf = (c: Colleague) =>
    operator !== null && c.email.toLowerCase() === operator.email.toLowerCase();
  const anyActions = rows.some((row) => !isSelf(row) && mayActOn(row.role));

  async function changeStatus(colleague: Colleague, next: ColleagueStatus) {
    if (inFlight.current.has(colleague.id)) return;
    inFlight.current.add(colleague.id);
    setBusyIds(new Set(inFlight.current));
    try {
      await saasAuthResellerApi.changeColleagueStatus(colleague.id, next);
      showToast(t(next === 'SUSPENDED' ? 'members.toast.suspended' : 'members.toast.reactivated'));
      reload();
    } finally {
      inFlight.current.delete(colleague.id);
      setBusyIds(new Set(inFlight.current));
    }
  }

  /** Reactivate is one click (it restores access); its error is the page's alert. */
  async function reactivate(colleague: Colleague) {
    setActionError(null);
    try {
      await changeStatus(colleague, 'ACTIVE');
    } catch (err) {
      setActionError(memberError(err));
    }
  }

  function dialogClosed() {
    setPending(null);
    focusReturn.dialogClosed();
  }

  function actionsFor(colleague: Colleague): RowAction[] {
    if (isSelf(colleague)) return [];
    if (!mayActOn(colleague.role)) return [];
    const suspended = colleague.status === 'SUSPENDED';
    return [
      {
        key: 'role',
        label: t('members.action.changeRole'),
        onSelect: () => setPending({ kind: 'role', colleague }),
      },
      {
        key: 'status',
        label: suspended ? t('members.action.reactivate') : t('members.action.suspend'),
        disabled: busyIds.has(colleague.id),
        onSelect: () =>
          suspended ? void reactivate(colleague) : setPending({ kind: 'suspend', colleague }),
      },
      {
        key: 'remove',
        label: t('members.action.remove'),
        onSelect: () => setPending({ kind: 'remove', colleague }),
      },
    ];
  }

  const target = pending?.colleague ?? null;

  return (
    <Box>
      <SearchField paged={paged} label={t('team.search')} />
      <ActionErrorAlert error={actionError} sx={{ mb: 2 }} />
      <Typography
        variant="h2"
        component="h2"
        id={headingId}
        tabIndex={-1}
        ref={heading}
        sx={visuallyHidden}
      >
        {t('team.tab.colleagues')}
      </Typography>
      {status === 'error' ? <ApiErrorState what={t('errors.what.team')} error={error} onRetry={reload} /> : null}
      {status === 'loading' && data === null ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={240} />
        </Box>
      ) : null}
      {status !== 'error' && data !== null && rows.length === 0 ? (
        q ? (
          <EmptyState kind="noMatch" title={t('members.noMatch')} onClear={paged.clear} />
        ) : (
          <EmptyState kind="none" title={t('team.empty')} />
        )
      ) : null}
      {status !== 'error' && data !== null && rows.length > 0 ? (
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-labelledby={headingId}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('members.col.name')}</TableCell>
                  <TableCell>{t('members.col.email')}</TableCell>
                  <TableCell>{t('members.col.role')}</TableCell>
                  <TableCell>{t('members.col.status')}</TableCell>
                  <TableCell>{t('team.col.mfa')}</TableCell>
                  <TableCell>{t('members.col.created')}</TableCell>
                  {anyActions ? (
                    <TableCell align="right">{t('members.col.actions')}</TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((colleague) => (
                  <TableRow key={colleague.id}>
                    <TableCell>{colleague.name ?? t('members.none')}</TableCell>
                    <TableCell component="th" scope="row" sx={{ overflowWrap: 'anywhere' }}>
                      {colleague.email}
                    </TableCell>
                    <TableCell>{roleLabel(colleague.role, t)}</TableCell>
                    <TableCell>
                      <StatusChip domain="member" status={colleague.status} />
                    </TableCell>
                    <TableCell>
                      {colleague.mfaEnabled ? t('team.mfaOn') : t('team.mfaOff')}
                    </TableCell>
                    <TableCell>{dayOf(colleague.createdAt, locale, t)}</TableCell>
                    {anyActions ? (
                      <TableCell align="right">
                        <RowActionsMenu
                          label={t('members.actionsFor', { who: colleague.email })}
                          actions={actionsFor(colleague)}
                          opener={focusReturn.opener}
                        />
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Pager paged={paged} count={data.totalElements} />
        </Paper>
      ) : null}
      <RoleDialog
        who={pending?.kind === 'role' ? (target?.email ?? null) : null}
        initial={target?.role ?? ''}
        options={grantable}
        onSubmit={async (role) => {
          if (!target) return;
          await saasAuthResellerApi.changeColleagueRole(target.id, role as ResellerRole);
          showToast(t('members.toast.roleChanged'));
          reload();
        }}
        onClose={dialogClosed}
        errorMessage={errorMessage}
      />
      <ConfirmDialog
        open={pending?.kind === 'suspend'}
        title={t('team.suspendTitle', { who: target?.email ?? '' })}
        consequence={t('team.suspendBody')}
        confirmLabel={t('members.action.suspend')}
        destructive
        onConfirm={async () => {
          if (target) await changeStatus(target, 'SUSPENDED');
        }}
        onCancel={dialogClosed}
        errorMessage={errorMessage}
      />
      <ConfirmDialog
        open={pending?.kind === 'remove'}
        title={t('team.removeTitle', { who: target?.email ?? '' })}
        consequence={t('team.removeBody')}
        confirmLabel={t('members.action.remove')}
        destructive
        onConfirm={async () => {
          if (!target) return;
          await saasAuthResellerApi.removeColleague(target.id);
          showToast(t('members.toast.removed'));
          focusReturn.rowGone();
          reload();
        }}
        onCancel={dialogClosed}
        errorMessage={errorMessage}
      />
    </Box>
  );
}
